// src/utils/randomGrid.js
// Generates a random, solvable target+player pair for the Memory Grid game.
// Colors are visually distinct across all 9 slots.
// Supports partial boards (some nulls) and full boards (boardFilled = true).

export const COLOR_PALETTE = [
  { id: 'lime',   bg: 'linear-gradient(135deg, #7AAF10 0%, #B5E917 100%)', glow: '#B5E917' },
  { id: 'cyan',   bg: 'linear-gradient(135deg, #0E7FA8 0%, #18B8E8 100%)', glow: '#18B8E8' },
  { id: 'pink',   bg: 'linear-gradient(135deg, #A3004D 0%, #FF0078 100%)', glow: '#FF0078' },
  { id: 'yellow', bg: 'linear-gradient(135deg, #C99A00 0%, #FFD930 100%)', glow: '#FFD930' },
  { id: 'purple', bg: 'linear-gradient(135deg, #4A1A8C 0%, #9B4EC4 100%)', glow: '#9B4EC4' },
  { id: 'orange', bg: 'linear-gradient(135deg, #8C4A1A 0%, #FF7A1A 100%)', glow: '#FF7A1A' },
  { id: 'red',    bg: 'linear-gradient(135deg, #6E0000 0%, #FF3030 100%)', glow: '#FF3030' },
  { id: 'teal',   bg: 'linear-gradient(135deg, #00595A 0%, #1AD9C4 100%)', glow: '#1AD9C4' },
  { id: 'white',  bg: 'linear-gradient(135deg, #888888 0%, #FFFFFF 100%)', glow: '#FFFFFF' },
];

export function getColorStyle(id) {
  return COLOR_PALETTE.find(c => c.id === id) || null;
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

/**
 * Generates a fresh { target, player } grid pair.
 *
 * @param {number} numColors  - How many distinct colors to place (3–9).
 * @param {number} gridSize   - Total cells in the board (always 9 for 3×3).
 * @param {boolean} boardFilled - When true, fill ALL gridSize cells with colors
 *                                (numColors colors are distributed evenly across all cells).
 *                                When false, only numColors cells are filled; rest are null.
 *
 * Guarantees:
 *  • Always solvable (player is a valid permutation of target pieces).
 *  • Never starts already-solved (player !== target).
 *  • Colors are evenly distributed (no color appears more than ceil(gridSize/numColors) times).
 *  • Works for both partial and full boards.
 */
export function generateGrid(numColors, gridSize = 9, boardFilled = false) {
  // Clamp numColors to palette size
  const safeColors = Math.min(Math.max(numColors, 1), COLOR_PALETTE.length);

  // Pick which colors to use (random subset of palette)
  const selectedColors = shuffleArray(COLOR_PALETTE.map(c => c.id)).slice(0, safeColors);

  let target;

  if (boardFilled) {
    // Fill all gridSize cells. Distribute colors as evenly as possible.
    // e.g. 9 cells, 6 colors → 3 colors get 2 cells each, 3 colors get 1 each.
    const colorList = [];
    for (let i = 0; i < gridSize; i++) {
      colorList.push(selectedColors[i % safeColors]);
    }
    target = shuffleArray(colorList);
  } else {
    // Partial board: exactly numColors cells filled, rest are null.
    // Place each color exactly once in a random position.
    const positions = shuffleArray([...Array(gridSize).keys()]).slice(0, safeColors);
    target = new Array(gridSize).fill(null);
    selectedColors.forEach((color, i) => {
      target[positions[i]] = color;
    });
  }

  // Build player grid: shuffle the same pieces so it's always solvable.
  // Keep retrying until player !== target (never start solved).
  let player;
  let attempts = 0;
  do {
    player = shuffleArray([...target]);
    attempts++;
    // Safety valve: after many tries on a trivial board, just swap two non-null elements
    if (attempts > 50) {
      player = [...target];
      const nonNullIdxs = player
        .map((v, i) => (v !== null ? i : -1))
        .filter(i => i !== -1);
      if (nonNullIdxs.length >= 2) {
        const a = nonNullIdxs[0];
        const b = nonNullIdxs[1];
        [player[a], player[b]] = [player[b], player[a]];
      }
      break;
    }
  } while (arraysEqual(player, target));

  return { target, player };
}